/**
 * What each chat mode whose pages are visible (overview, channels, creatives, audience, looker) is told to be.
 *
 * A card edits the role statement and the analysis method. Each page's data-reading rules
 * (`pageRules`) are code: the chat appends them after the card's text on every call, so a saved
 * card can neither change nor freeze them (spec 2026-10-20 page skills §2.2, §6).
 *
 * Laid out like /admin/ai/models, which solves a neighbouring problem: a value
 * that lives in code until a master overrides it. Each card states which of the
 * two it is showing — shipped wording, or an edit — rather than leaving that to
 * be inferred from whether a field looks filled in.
 *
 * There is no "add" here on purpose. A new mode needs a page that mounts the
 * chat with it, so adding one is code; what a master can change is what the
 * modes shown here say.
 *
 * The title, the subtitle and the guided questions were hidden on 2026-10-20
 * (owner) because no page reads them, and their columns are kept, so a save
 * still sends each one's stored value or, without a row, the code's.
 *
 * The Performance and Insights cards were hidden on 2026-10-20 (owner), and the
 * MMM card on 2026-10-30 (D5 R22), because every page that mounts their chat is
 * hidden; see SHOWN_MODES.
 *
 * @module pages/admin/AgentProfiles
 */

import React, { useCallback, useEffect, useId, useState } from "react";
import { Bot, Loader2, RotateCcw } from "lucide-react";
import { YDMPageHero } from "@/components/brand";
import { getAgentProfile } from "@/config/agentProfiles";
import {
  AGENT_MODES,
  PROFILE_LIMITS,
  clearAgentProfileCache,
  fetchAgentProfileOverrides,
  resetAgentProfile,
  saveAgentProfile,
  type AgentMode,
  type AgentProfileOverride,
} from "@/services/agentProfileService";
import type { GuidedQuestion } from "@/services/mediaChatService";

/**
 * The page that mounts each mode's chat; a mode whose pages are all hidden says so
 * (rules-04 D8). Since spec 2026-10-20 page skills §6, /overview and /channels each
 * have their own mode, so each shown card names one page. The hidden cards,
 * Performance, Insights and MMM, are not shown (SHOWN_MODES); their labels stay, so
 * the record is complete and a restore is one line.
 */
const MODE_LABEL: Record<AgentMode, string> = {
  performance: "Performance — หน้าแดชบอร์ดและ /campaigns (หน้าซ่อนอยู่)",
  mmm: "MMM — หน้า /mmm",
  insights: "Insights — หน้า /insights (หน้าซ่อนอยู่)",
  looker: "Looker — หน้า /looker",
  overview: "Overview — หน้า /overview",
  channels: "Channel Deep-dive — หน้า /channels",
  creatives: "Creative Performance — หน้า /creatives",
  audience: "Audience / Targeting — หน้า /audience",
};

/**
 * The cards this page shows, in menu order. The Looker card is shown whatever
 * VITE_LOOKER_ENABLED says (owner, 2026-10-21): with the flag off only the menu row
 * hides, /looker still opens by URL, and a saved looker row would drive that chat
 * with no editor and no reset. Performance and Insights were hidden on 2026-10-20
 * (owner) because every page that mounts their chat is hidden; their rows, their
 * code defaults and the service's AGENT_MODES guard are kept, and adding one back
 * here restores it. Creative Performance and Audience / Targeting each joined when
 * its page mounted its chat (spec 2026-10-21 d3-d4 chat §6). MMM left on 2026-10-30
 * for the same reason as Performance and Insights: its chat mounts only on /mmm,
 * which D5 hid (R22), and /budget has no chat (spec 2026-10-28 §11).
 */
const SHOWN_MODES: readonly AgentMode[] = ["overview", "channels", "creatives", "audience", "looker"];

/** The form's working copy of one card. */
interface Draft {
  title: string;
  subtitle: string;
  roleStatement: string;
  analysisTips: string;
  guidedQuestions: GuidedQuestion[];
  /** Whether a stored row exists, so the card can say which wording is live. */
  overridden: boolean;
  updatedAt: string | null;
}

const draftFor = (mode: AgentMode, override: AgentProfileOverride | undefined): Draft => {
  const base = getAgentProfile(mode);
  return {
    title: override?.title ?? base.title,
    subtitle: override?.subtitle ?? base.subtitle,
    roleStatement: override?.roleStatement ?? base.roleStatement,
    analysisTips: override?.analysisTips ?? base.analysisTips,
    guidedQuestions: override?.guidedQuestions.length ? override.guidedQuestions : base.guidedQuestions,
    overridden: Boolean(override),
    updatedAt: override?.updatedAt ?? null,
  };
};

const Counter: React.FC<{ value: string; max: number }> = ({ value, max }) => (
  <span className={`text-xs ${value.length > max ? "text-error-700" : "text-neutral-700"}`}>
    {value.length} / {max}
  </span>
);

export const AdminAgentProfiles: React.FC = () => {
  const [drafts, setDrafts] = useState<Record<AgentMode, Draft> | null>(null);
  const [busy, setBusy] = useState<AgentMode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<AgentMode | null>(null);
  // One base id, suffixed per card below: a hook cannot run inside the map or after the early return.
  const idBase = useId();

  const load = useCallback(async () => {
    const overrides = await fetchAgentProfileOverrides();
    // Every mode, so a mode added to the registry gets a draft without an edit here.
    setDrafts(Object.fromEntries(AGENT_MODES.map((m) => [m, draftFor(m, overrides.get(m))])) as Record<AgentMode, Draft>);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const update = (mode: AgentMode, patch: Partial<Draft>) =>
    setDrafts((prev) => (prev ? { ...prev, [mode]: { ...prev[mode], ...patch } } : prev));

  const onSave = async (mode: AgentMode) => {
    if (!drafts) return;
    const d = drafts[mode];
    setBusy(mode);
    setError(null);
    setSaved(null);

    const result = await saveAgentProfile({
      mode,
      title: d.title,
      subtitle: d.subtitle,
      roleStatement: d.roleStatement,
      analysisTips: d.analysisTips,
      guidedQuestions: d.guidedQuestions,
    });

    if (result.ok) {
      // Without this the master waits out the read cache before seeing their own
      // edit in the chat, and reasonably concludes the save did not work.
      clearAgentProfileCache();
      setSaved(mode);
      await load();
    } else {
      setError(result.error ?? "บันทึกไม่สำเร็จ");
    }
    setBusy(null);
  };

  const onReset = async (mode: AgentMode) => {
    setBusy(mode);
    setError(null);
    setSaved(null);
    const result = await resetAgentProfile(mode);
    if (result.ok) {
      clearAgentProfileCache();
      await load();
    } else {
      setError(result.error ?? "คืนค่าเริ่มต้นไม่สำเร็จ");
    }
    setBusy(null);
  };

  if (!drafts) {
    return (
      <div className="space-y-4 animate-fade-in">
        <YDMPageHero eyebrow="SUPER ADMIN" headline="AI SKILLS" subtitle="กำลังโหลด…" />
        <Loader2 className="w-5 h-5 animate-spin text-neutral-700" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <YDMPageHero
        eyebrow="SUPER ADMIN"
        headline="AI SKILLS"
        subtitle="แก้ตัวตนและวิธีวิเคราะห์ของผู้ช่วยแต่ละตัว มีผลกับทุกเวิร์กสเปซ"
      />

      {/* What a reader must know before typing: this text is added to the
          prompt, each page's data-reading rules and the grounding rules are
          appended after it, and a save puts the card's text in place of the
          code's until it is reset. */}
      <p className="text-xs text-neutral-700">
        ตัวตนและวิธีวิเคราะห์ที่แก้ที่นี่จะถูกใส่เข้าไปใน prompt ของผู้ช่วย และมีผลกับทุกเวิร์กสเปซตั้งแต่คำถามถัดไป (ภายใน 1 นาที)
        กฎการอ่านข้อมูลของแต่ละหน้าและกฎเรื่องห้ามแต่งตัวเลขจะถูกต่อท้ายเสมอ จึงไม่ถูกเขียนทับด้วยข้อความนี้ · เมื่อกดบันทึก
        ข้อความบนการ์ดจะใช้แทนข้อความในโค้ด แม้โค้ดจะปรับข้อความเริ่มต้นภายหลัง จนกว่าจะกด “คืนค่าเริ่มต้น”
      </p>

      {error && (
        <div role="alert" className="rounded-lg border border-error-200 bg-error-50 p-3 text-sm text-error-800">
          {error}
        </div>
      )}

      {SHOWN_MODES.map((mode) => {
        const d = drafts[mode];
        const isBusy = busy === mode;
        const methodLabelId = `${idBase}-${mode}-method`;
        const methodNoteId = `${idBase}-${mode}-method-note`;
        return (
          <section key={mode} className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3">
            <header className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Bot className="w-4 h-4 text-neutral-700" aria-hidden="true" />
                <h2 className="text-sm font-semibold text-ink-900">{MODE_LABEL[mode]}</h2>
              </div>
              <span className="text-xs text-neutral-700">
                {d.overridden ? "ใช้ข้อความที่แก้ไว้" : "ใช้ข้อความเริ่มต้นจากโค้ด"}
              </span>
            </header>

            <label className="block">
              <span className="flex items-center justify-between text-xs text-neutral-700">
                ตัวตน (role statement) <Counter value={d.roleStatement} max={PROFILE_LIMITS.roleStatement} />
              </span>
              <textarea
                value={d.roleStatement}
                onChange={(e) => update(mode, { roleStatement: e.target.value })}
                rows={4}
                className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-mono"
              />
            </label>

            {/* Named by the label span alone and described by the note: inside the <label>, the
                counter and the note would otherwise be read as part of the box's name. */}
            <label className="block">
              <span className="flex items-center justify-between text-xs text-neutral-700">
                <span id={methodLabelId}>วิธีวิเคราะห์ (analysis method)</span>
                <Counter value={d.analysisTips} max={PROFILE_LIMITS.analysisTips} />
              </span>
              <span id={methodNoteId} className="block text-xs text-neutral-700">
                กฎการอ่านข้อมูลของหน้านี้อยู่ในโค้ด และถูกต่อท้ายให้ทุกครั้ง — แก้จากที่นี่ไม่ได้
              </span>
              <textarea
                value={d.analysisTips}
                onChange={(e) => update(mode, { analysisTips: e.target.value })}
                aria-labelledby={methodLabelId}
                aria-describedby={methodNoteId}
                rows={8}
                className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm font-mono"
              />
            </label>

            <footer className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={() => void onSave(mode)}
                disabled={isBusy}
                className="rounded-lg bg-ink-800 px-3 py-1.5 text-sm text-white disabled:opacity-50"
              >
                {isBusy ? "กำลังบันทึก…" : "บันทึก"}
              </button>

              {/* Only offered when there is something to undo — a reset with no
                  stored row does nothing, and a button that does nothing is the
                  kind this codebase removes. */}
              {d.overridden && (
                <button
                  type="button"
                  onClick={() => void onReset(mode)}
                  disabled={isBusy}
                  className="flex items-center gap-1.5 rounded-lg border border-neutral-200 px-3 py-1.5 text-sm text-neutral-700 disabled:opacity-50"
                >
                  <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
                  คืนค่าเริ่มต้น
                </button>
              )}

              {saved === mode && <span className="text-xs text-emerald-700">บันทึกแล้ว</span>}
              {d.updatedAt && (
                <span className="ml-auto text-xs text-neutral-700">
                  แก้ล่าสุด {new Date(d.updatedAt).toLocaleString("th-TH")}
                </span>
              )}
            </footer>
          </section>
        );
      })}
    </div>
  );
};

export default AdminAgentProfiles;
